import { AlertTriangle, CheckCircle2, Download, FileSignature, Lock } from "lucide-react"

import { DataState } from "@/components/DataState"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { formatAmount, formatDate, formatMoney, formatPercent, formatPeriod } from "@/lib/format"
import { useI18n, t } from "@/lib/i18n"
import { FILING_STATUS_LABELS, type Filing, type FilingDetail } from "@/lib/types"
import { cn } from "@/lib/utils"
import {
  archivoVacio,
  casillasSinVerificar,
  contarPorSeveridad,
  describirArchivo,
  esLibroElectronico,
  type Declaracion,
  type FormularioDeclaracion,
} from "@/routes/accountant/declaracion"
import { BotonDescargar } from "@/components/BotonDescargar"

interface FilingDetailDialogProps {
  filing: Filing | null
  /** `GET /filings/{id}`. `null` mientras carga o si fallo: ver `detailLoading`. */
  detail?: FilingDetail | null
  detailLoading?: boolean
  detailError?: unknown
  onRetryDetail?: () => void
  open: boolean
  onOpenChange: (open: boolean) => void
  canSign: boolean
  signBlockedReason?: string
  onSign: () => void
  onObserve: () => void
  /** `GET /filings/{id}/export`: los formularios ARMADOS por el rulepack. */
  declaracion?: Declaracion | null
  declaracionLoading?: boolean
  declaracionError?: unknown
  onRetryDeclaracion?: () => void
  /** Baja el TXT oficial. Recibe el código del formulario y el nombre exacto. */
  onDescargar?: (filingCode: string, nombre: string) => void
  /** `period_id` y su tenant, para bajar la declaración COMO HOJA. */
  periodId?: string
  periodTenantId?: string
}

/**
 * Por declaracion: el cuadre por libro, la lista de excepciones (que necesito
 * criterio y por que) y el diff contra el mes anterior. Eso es lo que permite
 * firmar en minutos en vez de rehacer el trabajo.
 *
 * DOS FUENTES, UNA PANTALLA. La fila viene de la bandeja (cross-tenant, barata)
 * y el detalle de `GET /filings/{id}` (por periodo, caro). Mientras el detalle
 * carga se muestra la fila; cuando llega, MANDA el detalle. No se mezclan: la
 * bandeja lee `balanced: true` y las excepciones a medias porque no puede saber
 * mas, y pisar el dato real con el provisional seria mostrar un cuadre inventado
 * justo en la pantalla donde se firma.
 */
export function FilingDetailDialog({
  filing,
  detail,
  detailLoading,
  detailError,
  onRetryDetail,
  open,
  onOpenChange,
  canSign,
  signBlockedReason,
  onSign,
  onObserve,
  declaracion,
  declaracionLoading,
  declaracionError,
  onRetryDeclaracion,
  onDescargar,
  periodId,
  periodTenantId,
}: FilingDetailDialogProps) {
  const { term } = useI18n()
  if (!filing) return null

  // El detalle manda en cuanto existe. Antes de eso, lo que trajo la bandeja.
  const exceptions = detail?.exceptions ?? filing.exceptions
  const diff = detail?.diff ?? filing.diff
  const openExceptions = exceptions.filter((exception) => !exception.resolved)
  const bookTotals = detail?.bookTotals ?? []
  /*
    LOS ARCHIVOS QUE SE SUBEN AL PORTAL, todos, desde el pie. Cesar: «deberíamos
    de poder descargarlas porque el contador luego debe subirlas a la plataforma
    de cada país con su proceso y firmarlo». Los libros electrónicos (PLE) son
    archivos y se bajan; el FV 621 es de casillas y se TRANSCRIBE —de ése se
    baja la hoja con el origen de cada casilla—. El botón lo dice cuando no hay
    archivo que bajar, en vez de quedarse mudo.
  */
  const archivosParaPresentar = (declaracion?.declaraciones ?? []).flatMap((f) =>
    f.archivos.map((a) => ({ codigo: f.codigo, nombre: a.nombre })))

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {filing.tenantName} · {filing.form}
          </DialogTitle>
          <DialogDescription>
            {term.identificador_fiscal} <span className="code">{filing.ruc}</span> {" "}{t("· periodo")}{" "}
            {formatPeriod(filing.period)}{" "}{t("· vence")}{" "}<span className="code">{formatDate(filing.dueDate)}</span> {" "}{t("· preparado por")}{" "}{filing.preparedBy}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={filing.status === "firmado" ? "success-soft" : "secondary"}>
            {FILING_STATUS_LABELS[filing.status]}
          </Badge>
          {/*
            Cuatro estados, no dos. "Cuadrados" es una afirmacion sobre los
            asientos y solo se puede hacer cuando llegaron: mientras el detalle
            carga se dice que se esta comprobando, y un periodo sin ningun
            asiento se dice asi —no cuadra ni descuadra, no hay nada—. Pintar
            "Libros cuadrados" sobre un periodo vacio es lo que un contador lee
            como "ya lo revise".
          */}
          {detailLoading && !detail ? (
            <Badge variant="outline">{t("Comprobando el cuadre…")}</Badge>
          ) : !detail ? (
            <Badge variant="outline">{t("Cuadre no verificado")}</Badge>
          ) : !detail.hasEntries ? (
            <Badge variant="outline">{t("Sin asientos en el periodo")}</Badge>
          ) : detail.balanced ? (
            <Badge variant="success-soft">{t("Libros cuadrados")}</Badge>
          ) : (
            <Badge variant="destructive">{t("Libros sin cuadrar")}</Badge>
          )}
          <Badge variant={openExceptions.length > 0 ? "warning-soft" : "outline"}>
            {openExceptions.length}{" "}{t("excepciones abiertas")}</Badge>
          {detail ? (
            <Badge variant="outline" title={t("Comprobantes del periodo por estado")}>
              {detail.documentCounts.reduce((total, d) => total + d.count, 0)} comprobantes
            </Badge>
          ) : null}
        </div>

        {detailError ? (
          <p
            role="alert"
            className="flex items-start gap-2 rounded-md border border-destructive/35 bg-destructive-soft p-2.5 text-2xs leading-4"
          >
            <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0 text-destructive" />
            <span>
              <strong>{t("No se pudo cargar el detalle del periodo.")}</strong> {" "}{t("Lo que se ve abajo es lo que trajo la bandeja: las excepciones y el diff pueden estar incompletos.")}{" "}
              {onRetryDetail ? (
                <button type="button" className="underline" onClick={onRetryDetail}>
                  {t("Reintentar")}</button>
              ) : null}
            </span>
          </p>
        ) : null}

        {/* La cifra que se firma, antes que cualquier pestana. */}
        <div className="flex flex-wrap items-stretch divide-x divide-border rounded-md border bg-surface-sunken">
          <Figure label={`${term.impuesto_ventas} debito`} value={filing.igvDebito} />
          <Figure label={`${term.impuesto_ventas} credito`} value={filing.igvCredito} />
          <Figure label={`${term.impuesto_ventas} a pagar`} value={filing.igvAPagar} emphasis />
          <Figure label={t("Pago a cuenta de renta")} value={filing.pagoACuenta} />
        </div>

        {signBlockedReason ? (
          <p
            role="alert"
            className="flex items-start gap-2 rounded-md border border-warning/35 bg-warning-soft p-2.5 text-2xs leading-4"
          >
            <Lock className="mt-px h-3.5 w-3.5 shrink-0 text-warning" />
            <span>
              <strong>{t("La firma esta bloqueada.")}</strong> {signBlockedReason}
            </span>
          </p>
        ) : null}

        <Tabs defaultValue="libros">
          <TabsList>
            <TabsTrigger value="libros">{t("Libros")}{bookTotals.length ? ` (${bookTotals.length})` : ""}</TabsTrigger>
            <TabsTrigger value="excepciones">
              {t("Excepciones (")}{exceptions.length})
            </TabsTrigger>
            <TabsTrigger value="diff">{t("Diff vs. mes anterior")}</TabsTrigger>
            <TabsTrigger value="formulario">
              {t("Declaración")}{declaracion?.declaraciones.length
                ? ` (${declaracion.declaraciones.length})`
                : ""}
            </TabsTrigger>
          </TabsList>

          {/*
            El cuadre por libro. Es la pestana que abre por defecto porque es la
            unica con cifras reales: el formulario sigue en ceros hasta que el
            backend calcule la liquidacion del periodo.
          */}
          <TabsContent value="libros">
            <DataState
              loading={Boolean(detailLoading) && !detail}
              error={detailError}
              empty={Boolean(detail) && bookTotals.length === 0}
              emptyMessage={t("El periodo no tiene ningun asiento.")}
              emptyHint={t("No hay nada que cuadrar todavia. Procesa los comprobantes del periodo antes de firmar.")}
              onRetry={onRetryDetail}
              rows={3}
              columns={[26, 18, 18, 12, 16]}
            >
              <div className="overflow-hidden rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("Libro")}</TableHead>
                      <TableHead className="w-32 text-right">{t("Debe")}</TableHead>
                      <TableHead className="w-32 text-right">{t("Haber")}</TableHead>
                      <TableHead className="w-24 text-right">{t("Asientos")}</TableHead>
                      <TableHead className="w-28">{t("Cuadre")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bookTotals.map((libro) => (
                      <TableRow key={libro.book}>
                        <TableCell className="font-medium">{libro.label}</TableCell>
                        <TableCell className="num">{formatAmount(libro.debit)}</TableCell>
                        <TableCell className="num">{formatAmount(libro.credit)}</TableCell>
                        <TableCell className="num">{libro.entries}</TableCell>
                        <TableCell>
                          {libro.balanced ? (
                            <Badge variant="success-soft">{t("Cuadra")}</Badge>
                          ) : (
                            <Badge variant="destructive">
                              {t("Difiere")}{" "}{formatAmount(libro.debit - libro.credit)}
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {detail && detail.documentCounts.length > 0 ? (
                <div className="flex flex-wrap items-center gap-1.5 pt-2">
                  <span className="eyebrow">{t("Comprobantes del periodo")}</span>
                  {detail.documentCounts.map((d) => (
                    <Badge
                      key={d.status}
                      variant={
                        d.status === "pendiente" || d.status === "en_revision"
                          ? "warning-soft"
                          : "outline"
                      }
                    >
                      {d.count} {d.status.replace(/_/g, " ")}
                    </Badge>
                  ))}
                </div>
              ) : null}
              <p className="pt-2 text-2xs leading-4 text-muted-foreground">
                {t("El libro tributario guarda AJUSTES, no importes contables: un gasto deducible al 100% no genera asiento ahi, y eso es correcto. Un libro que no cuadra bloquea la firma.")}</p>
            </DataState>
          </TabsContent>

          <TabsContent value="formulario" className="space-y-2">
            <DataState
              loading={Boolean(declaracionLoading) && !declaracion}
              error={declaracionError}
              empty={
                !declaracionLoading &&
                !declaracionError &&
                Boolean(declaracion) &&
                declaracion!.declaraciones.length === 0
              }
              emptyMessage={t("Este período no produce formularios.")}
              emptyHint={
                declaracion?.motivo_sin_formularios ??
                "El rulepack de este país todavía no genera declaraciones."
              }
              onRetry={onRetryDeclaracion}
              columns={[30, 20, 25, 25]}
            >
              {declaracion && declaracion.declaraciones.length > 0 ? (
                <div className="space-y-2.5">
                  {declaracion.declaraciones.map((f) => (
                    <Formulario
                      key={f.codigo}
                      f={f}
                      onDescargar={onDescargar}
                    />
                  ))}
                  <p className="text-2xs leading-4 text-muted-foreground">
                    {declaracion.nota}
                  </p>
                </div>
              ) : null}
            </DataState>
          </TabsContent>

          <TabsContent value="excepciones" className="space-y-1.5">
            {detailLoading && !detail ? (
              <p className="rounded-md border border-dashed p-6 text-center text-xs text-muted-foreground">
                {t("Calculando las excepciones del periodo…")}</p>
            ) : exceptions.length === 0 ? (
              <p className="rounded-md border border-dashed p-6 text-center text-xs text-muted-foreground">
                {detail
                  ? "Sin excepciones: todo se resolvio por regla o memoria. No queda nada que requiera criterio humano en este periodo."
                  : "No se pudo cargar el detalle, asi que no se sabe si hay excepciones. No firmes sin verlo."}
              </p>
            ) : (
              exceptions.map((exception) => (
                <div
                  key={exception.id}
                  className={cn(
                    "rounded-md border p-2",
                    exception.resolved
                      ? "opacity-60"
                      : exception.severity === "alta"
                        ? "border-destructive/40 bg-destructive-soft"
                        : exception.severity === "media"
                          ? "border-warning/40 bg-warning-soft"
                          : "",
                  )}
                >
                  <div className="flex items-start gap-2">
                    {exception.resolved ? (
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                    ) : (
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="text-xs font-semibold">{exception.title}</span>
                        <Badge
                          variant={
                            exception.severity === "alta"
                              ? "destructive-soft"
                              : exception.severity === "media"
                                ? "warning-soft"
                                : "outline"
                          }
                        >
                          severidad {exception.severity}
                        </Badge>
                        <Badge variant="outline">{t("origen:")}{" "}{exception.source}</Badge>
                        {exception.documentRef ? (
                          <span className="code text-2xs text-muted-foreground">
                            {exception.documentRef}
                          </span>
                        ) : null}
                        {exception.amount !== undefined ? (
                          <span className="code text-2xs font-medium">
                            {formatMoney(exception.amount)}
                          </span>
                        ) : null}
                      </div>
                      <p className="mt-0.5 text-2xs leading-4 text-muted-foreground">
                        {exception.detail}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </TabsContent>

          <TabsContent value="diff">
            <div className="overflow-hidden rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("Concepto")}</TableHead>
                  <TableHead className="w-32 text-right">{t("Mes anterior")}</TableHead>
                  <TableHead className="w-32 text-right">{t("Este mes")}</TableHead>
                  <TableHead className="w-28 text-right">{t("Variacion")}</TableHead>
                  <TableHead className="w-20 text-right">%</TableHead>
                  <TableHead>{t("Nota")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {diff.map((row) => (
                  <TableRow key={row.concept}>
                    <TableCell>{row.concept}</TableCell>
                    <TableCell className="num">{formatAmount(row.previous)}</TableCell>
                    <TableCell className="num">{formatAmount(row.current)}</TableCell>
                    <TableCell
                      className={cn(
                        "num font-medium",
                        row.delta > 0 ? "text-warning" : row.delta < 0 ? "text-book-statutory" : "",
                      )}
                    >
                      {row.delta > 0 ? "+" : ""}
                      {formatAmount(row.delta)}
                    </TableCell>
                    <TableCell className="num">
                      {row.deltaPct === null ? "" : formatPercent(row.deltaPct, 1)}
                    </TableCell>
                    <TableCell className="text-2xs text-muted-foreground">
                      {row.note ?? ""}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
            {diff.length === 0 ? (
              <p className="pt-2 text-2xs leading-4 text-muted-foreground">
                {detailLoading && !detail
                  ? "Comparando contra el mes anterior…"
                  : detail
                    ? "No hay con que comparar: el periodo no tiene asientos, o es el primero de la entidad."
                    : "No se pudo cargar el detalle: el diff contra el mes anterior no esta disponible."}
              </p>
            ) : null}
            <p className="pt-2 text-2xs leading-4 text-muted-foreground">
              {t("Una variacion grande sin nota es lo que hay que mirar antes de firmar: la nota la escribe el motor cuando sabe explicarla. El motor marca por su cuenta todo lo que se mueva 30% o mas.")}</p>
          </TabsContent>
        </Tabs>

        <DialogFooter className="items-center">
          {/*
            UNA DESCARGA AQUÍ, Y NO DOS. «Como hoja» es la declaración en Excel
            —casilla, importe y DE DÓNDE SALE cada una—, que es lo que se firma y
            se archiva, y lo que permite contestar «¿de dónde salió esta
            casilla?» seis meses después. Cesar: «también las declaraciones,
            porque esas declaraciones seguro van a tener que firmarlas y luego
            recién subirlas a la plataforma de impuestos».

            Al lado hubo un botón «Exportar archivo oficial» que no exportaba
            nada: mostraba un aviso de «listo / todavía no» y mandaba a la
            pestaña «Declaración», donde ya están los botones que descargan cada
            archivo del fisco. Un botón con icono de descarga que no descarga es
            una promesa rota en el momento más delicado del mes.
          */}
          <div className="flex flex-wrap items-center gap-1.5 sm:mr-auto">
            <Button
              variant="outline"
              size="sm"
              disabled={!onDescargar || archivosParaPresentar.length === 0}
              title={archivosParaPresentar.length === 0
                ? t("Este período sólo tiene formularios de casillas: se transcriben en el portal. Descárgalo «como hoja» para tener cada casilla con su origen.")
                : `${archivosParaPresentar.length} ${t("archivo(s) oficiales, listos para cargar en el portal")}`}
              onClick={() => archivosParaPresentar.forEach((a) => onDescargar?.(a.codigo, a.nombre))}
            >
              <Download />
              {t("Descargar archivos para presentar")}
            </Button>
            {periodId ? (
              <BotonDescargar
                recurso="declaracion"
                etiqueta={t("Como hoja")}
                filtros={{ period_id: periodId }}
                tenantId={periodTenantId}
              />
            ) : null}
          </div>
          <Button variant="outline" size="sm" onClick={onObserve}>
            {t("Observar con motivo")}</Button>
          <Button
            size="sm"
            disabled={!canSign}
            onClick={onSign}
            title={signBlockedReason ?? "Firma la declaracion y cierra el periodo"}
          >
            <FileSignature />
            {t("Firmar y cerrar periodo")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Cifra de cabecera del formulario: etiqueta chica, numero grande y tabular. */
function Figure({
  label,
  value,
  emphasis,
}: {
  label: string
  value: number
  emphasis?: boolean
}) {
  return (
    <div className="min-w-[9rem] flex-1 px-3 py-2">
      <div className="eyebrow">{label}</div>
      <div
        className={cn(
          "num mt-0.5 text-left",
          emphasis ? "text-lg font-semibold text-foreground" : "text-sm text-muted-foreground",
        )}
      >
        {formatAmount(value)}
      </div>
    </div>
  )
}


/**
 * Un formulario armado: sus casillas o su archivo, y lo que no se pudo saber.
 *
 * DOS COSAS DISTINTAS BAJO EL MISMO NOMBRE. El FV 621 es un formulario de
 * CASILLAS que se transcribe en el portal; el PLE es un LIBRO ELECTRÓNICO que se
 * sube como archivo. Mezclarlos en una sola tabla haría que el contador buscara
 * un botón de descarga donde no lo hay, o transcribiera un libro de quinientas
 * líneas.
 */
function Formulario({
  f,
  onDescargar,
}: {
  f: FormularioDeclaracion
  onDescargar?: (filingCode: string, nombre: string) => void
}) {
  const sinVerificar = casillasSinVerificar(f)
  const { bloqueantes, advertencias } = contarPorSeveridad(f.excepciones ?? [])

  return (
    <section className="overflow-hidden rounded-md border">
      <header className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b bg-surface-sunken/60 px-2.5 py-1.5">
        <h3 className="text-2xs font-semibold uppercase tracking-[0.09em] text-foreground">
          {f.nombre}
        </h3>
        <Badge variant="outline" className="code">
          {f.codigo}
        </Badge>
        {f.vence ? (
          <span className="text-2xs text-muted-foreground">vence {formatDate(f.vence)}</span>
        ) : null}
        {bloqueantes > 0 ? (
          <Badge variant="destructive-soft" estado>
            {bloqueantes} bloqueante{bloqueantes === 1 ? "" : "s"}
          </Badge>
        ) : null}
        {advertencias > 0 ? (
          <Badge variant="warning-soft" estado>
            {advertencias} advertencia{advertencias === 1 ? "" : "s"}
          </Badge>
        ) : null}
      </header>

      {esLibroElectronico(f) ? (
        <div className="space-y-1.5 p-2.5">
          {f.archivos.map((a) => (
            <div key={a.nombre} className="flex flex-wrap items-center gap-2">
              <Button
                size="xs"
                variant="outline"
                onClick={() => onDescargar?.(f.codigo, a.nombre)}
                disabled={!onDescargar}
              >
                <Download />
                {t("Descargar")}</Button>
              <span className="code min-w-0 flex-1 truncate text-2xs" title={a.nombre}>
                {a.nombre}
              </span>
              <span className="text-2xs text-muted-foreground">{describirArchivo(a)}</span>
              {archivoVacio(a) ? (
                <span className="inline-flex items-center gap-1 text-2xs text-warning">
                  <AlertTriangle className="h-3 w-3" />
                  {t("Sin ninguna línea. Es un archivo válido y se presentaría igual.")}</span>
              ) : null}
            </div>
          ))}
          <p className="text-2xs leading-4 text-muted-foreground">
            {t("El nombre del archivo ES parte del formato: lo construye el sistema con el identificador fiscal, el período y los indicadores del libro. No lo renombres. Valídalo con el programa oficial antes de enviarlo.")}</p>
        </div>
      ) : (
        <>
          <Table className="table-dense">
            <TableHeader>
              <TableRow>
                <TableHead className="w-20">{t("Casilla")}</TableHead>
                <TableHead>{t("Concepto")}</TableHead>
                <TableHead className="w-36 text-right">{t("Importe")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {f.casillas.map((c) => (
                <TableRow key={c.codigo}>
                  <TableCell className="code">
                    <span className="inline-flex items-center gap-1">
                      {c.codigo}
                      {!c.layout_verificado ? (
                        <AlertTriangle
                          className="h-3 w-3 text-warning"
                          aria-label={t("posición sin verificar")}
                        />
                      ) : null}
                    </span>
                  </TableCell>
                  <TableCell className="text-2xs leading-4" title={c.origen ?? undefined}>
                    {c.etiqueta}
                  </TableCell>
                  <TableCell className={cn("num", Number(c.valor) === 0 && "num-zero")}>
                    {formatAmount(Number(c.valor))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {sinVerificar.length > 0 ? (
            <p className="border-t bg-warning-soft/30 px-2.5 py-1.5 text-2xs leading-4 text-foreground">
              <strong className="font-medium">
                {sinVerificar.length} casilla{sinVerificar.length === 1 ? "" : "s"}{" "}{t("con la posición sin cotejar")}</strong>{" "}
              {t("contra el formulario oficial vigente. El IMPORTE puede estar bien y el número de casilla ser el de otra versión: transcrito así, declara la cifra correcta en el renglón equivocado, y ninguna suma se descuadra. Coteja")}{" "}
              <span className="code">{sinVerificar.map((c) => c.codigo).join(", ")}</span> {" "}{t("antes de transcribir.")}</p>
          ) : null}
        </>
      )}

      {f.excepciones.length > 0 ? (
        <ul className="space-y-1 border-t p-2.5">
          {f.excepciones.map((e, i) => (
            <li
              key={`${e.codigo}-${i}`}
              className={cn(
                "text-2xs leading-4",
                e.severidad === "BLOCKER" ? "text-destructive" : "text-muted-foreground",
              )}
            >
              <Badge
                variant={
                  e.severidad === "BLOCKER"
                    ? "destructive-soft"
                    : e.severidad === "WARNING"
                      ? "warning-soft"
                      : "outline"
                }
                className="mr-1.5"
                estado
              >
                {e.codigo}
              </Badge>
              {e.mensaje}
              {e.accion ? <span className="text-muted-foreground"> {e.accion}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  )
}
